import React from "react";
import TradesTable from "src/components/TradesTable";
import { Card } from "src/components/ui/card";
import { useTradesTable } from "src/hooks/useTradesTable";
import { TradeResponse } from "src/model/client.schemas";
import {
  TradeOrActivity,
  getAccountActivityItems,
  isActivity,
} from "src/util/activity_util";
import { Badge } from "src/components/ui/badge";
import { ATTRIBUTE_TO_NAME } from "src/util/trades_table_config";
import {
  DateRangeFilter,
  FilterManager,
  MultiSelectionFilter,
  SelectedValueChip,
  SingleSelectionFilter,
} from "src/util/attribute_filter";
import SearchMultiSelect from "src/components/filters/SearchMultiSelect";
import DropdownFilter from "src/components/filters/DropdownFilter";
import DateRangeFilterComponent from "src/components/filters/DateRangeFilter";
import AttributeFilterSidebar from "src/components/filters/AttributeFilterSidebar";
import NaturalLanguageFilter from "src/components/filters/NaturalLanguageFilter";
import TradesStatsPanel from "src/components/TradesStatsPanel";
import { getAppDataGetAppDataQueryOptions } from "src/model/app-data";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "src/context/AccountContext";
import { getGetAppDataWithParamsQueryOptions } from "src/util/query_client";
import { useSuspenseQuery } from "@tanstack/react-query";
import LoadingOverlay from "src/components/LoadingOverlay";
import { Button } from "@/components/ui/button";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { normalizeTrades } from "@/util/unifiedTradesApi";

const SIDE_DROPDOWN_OPTIONS: { [key: string]: string } = {
  long: ATTRIBUTE_TO_NAME["side"]["long"],
  short: ATTRIBUTE_TO_NAME["side"]["short"],
};

export const Route = createFileRoute("/_app/trades")({
  component: TradesPageWrapper,
  staticData: {
    title: "Trades",
  },
  pendingComponent: () => <LoadingOverlay isLoading={true} />,
});

interface TradesFiltersPanelProps {
  tableItems: TradeOrActivity[];
  filterManager: FilterManager<TradeResponse>;
}

const TradesFiltersPanel: React.FC<TradesFiltersPanelProps> = ({
  tableItems,
  filterManager,
}) => {
  // Extract just the trades for filter operations
  const trades = tableItems.filter(
    (item) => !isActivity(item),
  ) as TradeResponse[];
  const filters: JSX.Element[] = [];
  const tradeSideFilter = filterManager.getOrCreateFilter("side", "single");

  if (tradeSideFilter) {
    filters.push(
      <DropdownFilter
        key="side"
        options={SIDE_DROPDOWN_OPTIONS}
        placeholder="Side"
        filter={tradeSideFilter as SingleSelectionFilter<TradeResponse>}
      />,
    );
  }

  const tradeStatusFilter = filterManager.getOrCreateFilter("status", "single");
  if (tradeStatusFilter) {
    filters.push(
      <DropdownFilter
        key="status"
        options={ATTRIBUTE_TO_NAME["status"]}
        placeholder="Status"
        filter={tradeStatusFilter as SingleSelectionFilter<TradeResponse>}
      />,
    );
  }

  const tickerFilter = filterManager.getOrCreateFilter("ticker", "multiple");
  if (tickerFilter) {
    const allTickers: string[] = Array.from(
      new Set(trades.map((trade) => trade.ticker)),
    );
    filters.push(
      <SearchMultiSelect
        key="ticker"
        allValues={allTickers}
        filter={tickerFilter as MultiSelectionFilter<TradeResponse>}
      />,
    );
  }

  const entryDateFilter = filterManager.getOrCreateFilter(
    "entryDate",
    "date_range",
  );
  if (entryDateFilter) {
    filters.push(
      <DateRangeFilterComponent
        key="entryDate"
        filter={entryDateFilter as DateRangeFilter<TradeResponse>}
      />,
    );
  }

  filters.push(
    <AttributeFilterSidebar
      key="attribute"
      trades={trades}
      filterManager={filterManager}
    />,
  );

  return (
    <Card className="card items-center p-5">
      <div className="mb-4 w-full">
        <NaturalLanguageFilter filterManager={filterManager} />
      </div>
      <div className="w-full items-center min-[75rem]:flex">
        <h3 className="text-2xl font-medium leading-loose">Filters</h3>
        <div className="ml-auto flex flex-wrap gap-5">{filters}</div>
      </div>
      <div
        className={
          filterManager.getAllChips().length > 0 ? "mt-5 flex flex-wrap" : ""
        }
      >
        {filterManager.getAllChips().map((selection: SelectedValueChip) => (
          <Badge
            key={`${selection.attribute}-${selection.value.value}`}
            variant="outline"
            className="my-1 mr-2 px-2.5 py-1.5 text-sm font-light dark:border-black dark:bg-medium-gray"
          >
            <span className="mr-2">{selection.value.display_value}</span>
            <button
              onClick={() =>
                filterManager.removeValue(
                  selection.attribute,
                  selection.value.value,
                )
              }
            >
              <span className="size-3 stroke-current">x</span>
            </button>
          </Badge>
        ))}
      </div>
    </Card>
  );
};

interface TradesPanelProps {
  tableItems: TradeOrActivity[];
}

const TradesPanel: React.FC<TradesPanelProps> = ({ tableItems }) => {
  // Extract just the trades for filter operations
  const {
    tradesTable,
    userSetups,
    filterManager,
    setColumnVisibilityState,
    setColumnOrderState,
    addTagColumn,
  } = useTradesTable(tableItems);

  return (
    <div className="flex w-full flex-col gap-5">
      <TradesFiltersPanel
        tableItems={tableItems}
        filterManager={filterManager}
      />
      <div className="hidden lg:block">
        <TradesStatsPanel
          tableItems={tableItems}
          filterManager={filterManager}
        />
      </div>
      <Card className="card">
        <TradesTable
          table={tradesTable}
          userSetups={userSetups}
          setColumnVisibilityState={setColumnVisibilityState}
          setColumnOrderState={setColumnOrderState}
          onAddTagColumn={addTagColumn}
        />
        <div className="mr-5 flex items-center justify-end space-x-4 py-4">
          <span className="text-sm text-muted-foreground dark:text-muted-foreground">
            Page {tradesTable.getState().pagination.pageIndex + 1} of{" "}
            {tradesTable.getRowModel().rows.length > 0
              ? tradesTable.getPageCount()
              : 1}
          </span>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => tradesTable.previousPage()}
              disabled={!tradesTable.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => tradesTable.nextPage()}
              disabled={!tradesTable.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

function TradesPageWrapper() {
  return <TradesPage />;
}

function TradesPage() {
  const { selectedAccount } = useAccount();
  const useV2Api = useFeatureFlag("use_v2_imports");

  // Choose query options based on feature flag
  // Trades page needs attributes for filtering
  const tradesQuery = useV2Api
    ? getGetAppDataWithParamsQueryOptions({ include_attributes: true })
    : getAppDataGetAppDataQueryOptions();

  // @ts-ignore
  const { data: tradesResponse } = useSuspenseQuery(tradesQuery);

  // Wait for the selected account to be set since we have accounts in the response.
  if (!selectedAccount && (tradesResponse.data.account_data?.length ?? 0) > 0) {
    return null;
  }

  // Normalize trades from either API version
  const normalizedTrades = normalizeTrades(tradesResponse?.data.all_trades);

  // Get combined table items (trades and cash activities) for the selected account
  const tableItems = getAccountActivityItems(
    normalizedTrades,
    undefined,
    selectedAccount,
  );

  return <TradesPanel tableItems={tableItems} />;
}
